import React, { useEffect, useState, useRef } from "react";
import { useTransactions } from "./useTransactions";
import "../css/FrameStyles.css";
import loadingAnimation from "../assets/Loading.json";
import Lottie from "lottie-react";
import SummaryCard from "./SummaryCard";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { MultiSelect } from "primereact/multiselect";
import { FilterMatchMode } from "primereact/api";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import cloneDeep from "lodash/cloneDeep";
import axios from "axios";
import CapitalSummaryCard from "./CapitalSummaryCard";
import { fetchTaxRates } from '../utils/taxRatesCache';

function UnrealizedCapitalGainsFrame({ username }) {
  const backendUrl = "https://profitree-backend-9482feeffc4a.herokuapp.com";
  const isMobileView = window.innerWidth <= 768;
  const tableRef = useRef(null);
  const [tableData, setTableData] = useState([]);
  const [transactionTypes] = useState([
    { name: "BUY", value: "BUY" },
    { name: "SELL", value: "SELL" },
    { name: "DIVIDEND", value: "DIVIDEND" },
    { name: "OPTION", value: "OPTION" },
  ]);
  const [taxTypes] = useState([
    { name: "Long Term", value: "Long Term" },
    { name: "Short Term", value: "Short Term" },
  ]);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.IN },
    taxType: { value: null, matchMode: FilterMatchMode.IN },
  });

  const toNumeric = (value) => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === "string") {
      const numeric = Number(value.replace(/[^0-9.-]/g, ""));
      return Number.isFinite(numeric) ? numeric : 0;
    }
    return 0;
  };

  const formatCurrencyValue = (amount) => {
    const numeric = toNumeric(amount);
    const formatted = Math.abs(numeric).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return numeric < 0 ? `-$${formatted}` : `$${formatted}`;
  };

  const formatQuantityDisplay = (qty) => {
    const numeric = toNumeric(qty);
    const fixed = Number(numeric.toFixed(2));
    if (Number.isInteger(fixed)) {
      return fixed.toString();
    }
    return fixed.toFixed(2).replace(/\.?0+$/, "");
  };

  const renderCurrentPriceCell = (rowData) => {
    const originalQty = toNumeric(rowData.quantity);
    const settledQty = toNumeric(rowData.settledQuantity);
    const remainingQty = Math.max(originalQty - settledQty, 0);
    const quantityLabel = formatQuantityDisplay(remainingQty);
    const priceLabel = formatCurrencyValue(rowData.currentPrice);

    return (
      <span className="table-value-with-annotation">
        {priceLabel}
        <span className="table-annotation">
          (
          <span
            className="table-tooltip-trigger remaining-quantity-tooltip"
            data-pr-tooltip={`Remaining quantity: ${quantityLabel}`}
            title={`Remaining quantity: ${quantityLabel}`}
          >
            {quantityLabel}
          </span>
          )
        </span>
      </span>
    );
  };

  const renderTickerCell = (rowData) => {
    const ticker = (rowData.tickerSymbol || rowData.ticker || "--").toString().trim();
    return <span className="table-highlight-text table-highlight-text--ticker">{ticker || "--"}</span>;
  };

  const renderUnrealizedProfitCell = (rowData) => {
    const profitValue = toNumeric(rowData.unrealizedProfitLoss);
    const displayValue = formatCurrencyValue(profitValue);

    const rawTaxType = rowData.taxType || "";
    const descriptorMatch = rawTaxType.match(/^[A-Za-z\s]+/);
    const descriptor = descriptorMatch ? descriptorMatch[0].trim() : rawTaxType;
    const abbreviation = descriptor ? descriptor.charAt(0).toUpperCase() : "–";

    const rateMatch = rawTaxType.match(/(\d+(\.\d+)?)%/);
    const rateLabel = rateMatch ? `${rateMatch[1]}%` : "";
    const tooltipText = descriptor
      ? `${descriptor}${rateLabel ? ` at ${rateLabel} tax rate` : ""}`
      : "Tax type unavailable";

    return (
      <span className="table-highlight-text table-highlight-text--value">
        <span className="table-value-with-annotation">
          {displayValue}
          <span className="table-annotation">
            (
            <span
              className="table-tooltip-trigger tax-type-tooltip"
              data-pr-tooltip={tooltipText}
              title={tooltipText}
            >
              {abbreviation}
            </span>
            )
          </span>
        </span>
      </span>
    );
  };
  const [yearFilter, setYearFilter] = useState(
    new Date().getFullYear().toString()
  );

  const [expandedRows, setExpandedRows] = useState(null);

  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const { data, loading, error } = useTransactions(
    "transactions/getPotentialGainOpportunities"
  );

  const [totalTaxesOwed, setTotalTaxesOwed] = useState("");
  const [totalCapitalGain, setTotalCapitalGain] = useState("");
  const [shortTermRealizedGain, setShortTermRealizedGain] = useState("");
  const [longTermRealizedGain, setLongTermRealizedGain] = useState("");
  const [totalDividends, setTotalDividends] = useState("");
  const [longTermTaxRate, setLongTermTaxRate] = useState(20); // Default value
  const [shortTermTaxRate, setShortTermTaxRate] = useState(30); // Default value

  // Fetch tax rates from backend
  const getTaxRates = async () => {
    if (!username) return;
    const rates = await fetchTaxRates(username);
    if (rates) {
      setLongTermTaxRate(rates.LONG_TERM);
      setShortTermTaxRate(rates.SHORT_TERM);
    }
  };

  const getSummaryCardsValue = (data = tableData) => {
    const shortTermUnrealizedGain = data
      .filter(t => t.taxType && t.taxType.startsWith("Short Term"))
      .reduce((acc, t) => {
        // Use the numeric value if available, otherwise parse from formatted string
        const numericValue = t.unrealizedProfitLossValue !== undefined 
          ? Number(t.unrealizedProfitLossValue)
          : (typeof t.unrealizedProfitLoss === 'string' 
            ? parseFloat(t.unrealizedProfitLoss.replace(/[$,]/g, '')) 
            : Number(t.unrealizedProfitLoss || 0));
        return acc + Math.abs(numericValue || 0);
      }, 0);

    const longTermUnrealizedGain = data
      .filter(t => t.taxType && t.taxType.startsWith("Long Term"))
      .reduce((acc, t) => {
        // Use the numeric value if available, otherwise parse from formatted string
        const numericValue = t.unrealizedProfitLossValue !== undefined 
          ? Number(t.unrealizedProfitLossValue)
          : (typeof t.unrealizedProfitLoss === 'string' 
            ? parseFloat(t.unrealizedProfitLoss.replace(/[$,]/g, '')) 
            : Number(t.unrealizedProfitLoss || 0));
        return acc + Math.abs(numericValue || 0);
      }, 0);

    setShortTermRealizedGain(shortTermUnrealizedGain);
    setLongTermRealizedGain(longTermUnrealizedGain);
  };

  function formatDescription(desc) {
    return desc ? desc.replace(/(\d+\.\d{3,})/g, (num) => Number(num).toFixed(2)) : '';
  }

  useEffect(() => {
    const fetchAndProcessData = async () => {
      if (username) {
        await getTaxRates(); // Ensure tax rates are fetched first
      }

      if (!loading && error === null) {
        // Only keep transactions with NO settlementTransactions (unrealized)
        let unrealizedData = data.filter(
          (item) => !item.settlementTransactions || item.settlementTransactions.length === 0
        );

        const processedData = unrealizedData.map((item) => {
          const tempItem = cloneDeep(item);
          const currentDate = new Date();
          const transactionDate = new Date(tempItem.date);
          const differenceInDays = Math.floor((currentDate - transactionDate) / (1000 * 60 * 60 * 24));

          const originalQty = toNumeric(item.quantity);
          const settledQty = toNumeric(item.settledQuantity);
          const remainingQty = Math.max(originalQty - settledQty, 0);

          tempItem.purchaseDate = item.date;
          const purchasePriceValue = item.price ? toNumeric(item.price) : 0;
          tempItem.purchasePriceValue = purchasePriceValue;
          tempItem.purchasePrice = formatCurrencyValue(purchasePriceValue);
          tempItem.soldPrice = formatCurrencyValue(purchasePriceValue);

          const currentPriceValue = item.currentPrice !== undefined ? Number(item.currentPrice) : 0;
          tempItem.currentPriceValue = currentPriceValue;
          tempItem.currentPrice = formatCurrencyValue(currentPriceValue);

          const isLongTerm = differenceInDays > 365;
          const taxRate = isLongTerm ? longTermTaxRate : shortTermTaxRate;
          tempItem.taxType = isLongTerm
            ? `Long Term (${Number(longTermTaxRate).toFixed(2)}%)`
            : `Short Term (${Number(shortTermTaxRate).toFixed(2)}%)`;

          tempItem.price = `$${Number(item.price).toFixed(2)}`;
          tempItem.quantity = originalQty;
          tempItem.settledQuantity = settledQty;
          tempItem.remainingQuantity = remainingQty;

          tempItem.profitLossWithoutPrefix = item.profitLoss;
          tempItem.profitLoss = `$${Number(item.profitLoss).toFixed(2)}`;

          const unrealizedValue =
            item.unrealizedProfitLoss !== undefined ? Number(item.unrealizedProfitLoss) : 0;
          tempItem.unrealizedProfitLossValue = unrealizedValue;
          tempItem.unrealizedProfitLoss = formatCurrencyValue(unrealizedValue);

          tempItem.taxesOwed = Math.abs(tempItem.profitLossWithoutPrefix) * (taxRate / 100);
          tempItem.daysToAvoidShortTermTax = !isLongTerm ? 365 - differenceInDays : "";
          tempItem.description = formatDescription(item.description || "");
          return tempItem;
        });

        setTableData(processedData);
        getSummaryCardsValue(processedData); // Update summary values
      }
    };

    fetchAndProcessData(); // Call the function
  }, [username, data, longTermTaxRate, shortTermTaxRate]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Lottie
          animationData={loadingAnimation}
          style={{ height: "150px", width: "150px" }} // Adjust loader size
        />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        Error: {error}, please contact support for help:
        support@profitree-tax.com
      </div>
    );
  }

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const clearFilter = () => {
    initFilters();
  };

  const initFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      type: { value: null, matchMode: FilterMatchMode.IN },
      taxType: { value: null, matchMode: FilterMatchMode.IN },
    });
    setGlobalFilterValue("");
    setYearFilter("");
    if (tableRef.current) {
      tableRef.current.reset();
    }
  };

  const renderHeader = () => {
    return (
      <div className="datatable-header-wrapper">
        <IconField>
          <Button
            type="button"
            icon="pi pi-filter-slash"
            label="Clear Filters"
            outlined
            onClick={clearFilter}
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            x="0px"
            y="0px"
            width="100"
            height="100"
            viewBox="0 0 50 50"
          >
            <path d="M 21 3 C 11.622998 3 4 10.623005 4 20 C 4 29.376995 11.622998 37 21 37 C 24.712383 37 28.139151 35.791079 30.9375 33.765625 L 44.085938 46.914062 L 46.914062 44.085938 L 33.886719 31.058594 C 36.443536 28.083 38 24.223631 38 20 C 38 10.623005 30.377002 3 21 3 z M 21 5 C 29.296122 5 36 11.703883 36 20 C 36 28.296117 29.296122 35 21 35 C 12.703878 35 6 28.296117 6 20 C 6 11.703883 12.703878 5 21 5 z"></path>
          </svg>
          <InputText
            value={globalFilterValue || ""}
            onChange={onGlobalFilterChange}
            placeholder="Search by keyword"
          />
        </IconField>
      </div>
    );
  };

  const itemTemplate = (option) => {
    return (
      <div className="flex align-items-center gap-2">
        <span>{option.name}</span>
      </div>
    );
  };

  const transactionTypeRowFilterTemplate = (options) => {
    return (
      <MultiSelect
        value={options.value}
        options={transactionTypes}
        itemTemplate={itemTemplate}
        onChange={(e) => options.filterApplyCallback(e.value)}
        optionLabel="name"
        placeholder="Any"
        className="p-column-filter"
        maxSelectedLabels={1}
      />
    );
  };

  const taxTypeRowFilterTemplate = (options) => {
    return (
      <MultiSelect
        value={options.value}
        options={taxTypes}
        itemTemplate={itemTemplate}
        onChange={(e) => options.filterApplyCallback(e.value)}
        optionLabel="name"
        placeholder="Any"
        className="p-column-filter"
        maxSelectedLabels={1}
      />
    );
  };

  const allowExpansion = (rowData) => {
    return rowData.settlementTransactions.length > 0 || isMobileView;
  };

  const transactionTypeBodyTemplate = (rowData) => {
    const base = rowData.type;
    let extra = "";
    //if (base === "OPTION" || base === "DIVIDEND") {
    if (rowData.description) {
      extra = `(${rowData.description})`;
    }
    if (rowData.optionType && rowData.optionSide && rowData.description) {
      extra = `(${rowData.optionType} ${rowData.optionSide}) - (${rowData.description})`;
    } else if (rowData.optionType && rowData.description) {
      extra = `(${rowData.optionType}) - (${rowData.description})`;
    } else if (rowData.description) {
      extra = `(${rowData.description})`;
    }

    return (
      <div>
        <div>{base}</div>
        {extra && <small style={{ color: "#666" }}>{extra}</small>}
      </div>
    );
    // }

    return <div>{base}</div>;
  };

  const rowExpansionTemplate = (data) => {
    return !isMobileView ? (
      <div className="p-3">
        <h5>Settlement info</h5>
        <DataTable value={data.settlementTransactions}>
          <Column field="instrument_id" header="Ticker" sortable></Column>
          <Column
            field="type"
            header="Transaction Type"
            style={{ maxWidth: "15rem", minWidth: "15rem" }}
            sortable
            body={transactionTypeBodyTemplate}
          ></Column>
          <Column field="costBasis" header="Purchase Price" sortable></Column>
          <Column
            field="quantity"
            header="Purchase Quantity"
            sortable
          ></Column>
          <Column
            field="settledQuantityForCurrentTransaction"
            header="Settled Quantity(Current Tx)"
            sortable
          ></Column>
          <Column field="date" header="Purchase Date" sortable></Column>
        </DataTable>
      </div>
    ) : (
      <div className="mobile-expand-info">
        <div className="expand-row">
          <h4>Account Name:</h4>
          <p>{data.accountName}</p>
        </div>
        <div className="expand-row">
          <h4>Ticker:</h4>
          <p>{data.tickerSymbol}</p>
        </div>
        <div className="expand-row">
          <h4>Transaction Type:</h4>
          <p>{data.type}</p>
        </div>

        <div className="expand-row">
          <h4>Purchase Date:</h4>
          <p>{data.purchaseDate}</p>
        </div>
        <div className="expand-row">
          <h4>Purchase Price:</h4>
          <p>{data.purchasePrice}</p>
        </div>
        <div className="expand-row">
          <h4>Current Price:</h4>
          <p>{data.currentPrice}</p>
        </div>
        <div className="expand-row">
          <h4>Unrealized Profit Amt:</h4>
          <p>{data.unrealizedProfitLoss}</p>
        </div>
        <div className="expand-row">
          <h4>Tax Type:</h4>
          <p>{data.taxType}</p>
        </div>
        <div className="expand-row">
          <h4>Days To Avoid Short term tax:</h4>
          <p>{data.daysToAvoidShortTermTax}</p>
        </div>
        <div className="expand-row">
          <h4>Description:</h4>
          <p>{data.description}</p>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="capital-summary-container">
        <CapitalSummaryCard
          title="Short-term Gains"
          value={shortTermRealizedGain}
          type={shortTermRealizedGain >= 0 ? 'positive' : 'negative'}
          tooltip="Gains from assets held for less than a year, taxed at your regular income tax rate"
        />
        <CapitalSummaryCard
          title="Long-term Gains"
          value={longTermRealizedGain}
          type={longTermRealizedGain >= 0 ? 'positive' : 'negative'}
          tooltip="Gains from assets held for more than a year, typically taxed at a lower capital gains rate"
        />
      </div>

      <Tooltip
        target=".remaining-quantity-tooltip"
        position="top"
        showDelay={100}
        hideDelay={100}
      />
      <Tooltip
        target=".tax-type-tooltip"
        position="top"
        showDelay={100}
        hideDelay={100}
      />

      <DataTable
        value={tableData}
        removableSort
        sortMode="multiple"
        paginator
        rows={10}
        dataKey="id"
        filters={filters}
        loading={loading}
        ref={tableRef}
        multiSortMeta={[
          {
            field: "unrealizedProfitLossValue",
            order: "-1",
          },
        ]}
        responsiveLayout="stack" // Enables stacking on smaller screens
        breakpoint="960px" // Define at what width stacking happens
        globalFilterFields={[
          "accountName",
          "tickerSymbol",
          "type",
          "purchaseDate",
          "date",
          "taxType",
        ]}
        header={renderHeader()}
        emptyMessage="No transactions found."
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        onValueChange={(e) => {
          getSummaryCardsValue(e);
        }}
      >
        <Column
          expander={allowExpansion}
          style={{ width: "1rem" }}
          className={isMobileView ? "mobile-row-expander" : ""}
        />
        {!isMobileView ? (
          <Column field="accountName" header="Account Name" sortable></Column>
        ) : null}
        <Column
          field="tickerSymbol"
          header="Ticker"
          sortable
          body={renderTickerCell}
          className="table-highlight-column-cell"
        ></Column>
        {!isMobileView ? (
          <Column
            field={"type"}
            header="Transaction Type"
            sortable
            showFilterMenu={true}
            style={{ maxWidth: "10rem", minWidth: "10rem" }}
            filter
            filterField="type"
            showFilterMatchModes={false}
            filterElement={transactionTypeRowFilterTemplate}
            filterMenuStyle={{ width: "14rem" }}
            body={transactionTypeBodyTemplate}
          ></Column>
        ) : null}
        {!isMobileView ? (
          <Column field="purchaseDate" header="Purchase Date" sortable></Column>
        ) : null}
        {!isMobileView ? (
          <Column
            field="purchasePrice"
            header="Purchase Price"
            sortable
            sortField="purchasePriceValue"
          ></Column>
        ) : null}
        {!isMobileView ? (
          <Column
            field="currentPriceValue"
            header="Current Price"
            body={renderCurrentPriceCell}
            sortable
            sortField="currentPriceValue"
          ></Column>
        ) : null}
        <Column
          field="unrealizedProfitLossValue"
          header="Unrealized Profit Amt"
          body={renderUnrealizedProfitCell}
          sortable
          sortField="unrealizedProfitLossValue"
          className="table-highlight-column-cell"
        ></Column>
        <Column
          field="daysToAvoidShortTermTax"
          header="Days To Avoid Short term tax"
          sortable
          body={(rowData) => (
            <span
              className={
                rowData.daysToAvoidShortTermTax
                  ? "table-highlight-text table-highlight-text--alt"
                  : "table-highlight-text table-highlight-text--muted"
              }
            >
              {rowData.daysToAvoidShortTermTax || "—"}
            </span>
          )}
          className="table-highlight-column-cell"
        ></Column>
      </DataTable>
    </div>
  );
}

export default UnrealizedCapitalGainsFrame;
